<template>
  <div :class="{ 'overview-container--loading': loading }" class="overview-container">
    <!-- SHOW ERROR -->
    <span v-if="!loading && showErrorTile" style="display: contents">
      <ErrorTile :title="$t('quoteTile.errorTitle')"></ErrorTile>
    </span>
    <!-- SHOW TILE -->
    <span v-if="!showErrorTile">
      <Card v-if="loading || !motorQuote" :loadingState="true"></Card>
      <Card
        v-if="motorQuote && !loading"
        :id="'quote.clickable-quote-card.' + quoteId"
        :iconClass="iconStringResolved"
        :title="firstTitle"
        :isPolicy="true"
        :isQuoteDetails="isDetailHeader"
        :isPolicyDetails="isDetailHeader"
      >
        <template #title-extra>{{ subline }} </template>

        <!-- CONTENT-->
        <template #content>
          <GridContent :with-padding="false">
            <template #gridContent>
              <dl class="info">
                <dt id="quoteNumberLabel" class="info__label">
                  {{ $t('quoteTile.quoteNumber') }}
                </dt>
                <dd id="quoteNumberValue" class="info__value">{{ quoteId }}</dd>
              </dl>
              <dl v-if="policyBeginning && isVehicle112">
                <dt id="quoteBeginningDatePolicyLabel" class="info__label">
                  {{ $t('quoteTile.beginningDatePolicy') }}
                </dt>
                <dd id="quoteBeginningDatePolicyValue" class="info__value">
                  {{ policyBeginning }}
                </dd>
              </dl>
              <dl v-if="isDetailHeader && payment()">
                <dt id="quotePaymentLabel" class="info__label">
                  {{ $t('quoteTile.payment.grossPremium') }}
                </dt>
                <dd id="quotePaymentValue" class="info__value">
                  {{ payment() }}
                </dd>
              </dl>
            </template>
          </GridContent>
        </template>

        <!-- FOOTER-->
        <template v-if="showFooter" #footer>
          <div v-if="!isDetailHeader" class="footer-cta footer-cta--left">
            <!-- Wenn 112, dann "Weitere Details" verfügbar-->
            <CustomButton
              v-if="isVehicle112"
              @click="goToQuoteDetailView"
              id="quote.details-button.112"
              :buttonColor="ButtonColor.SECONDARY"
              :disabledState="false"
            >
              {{ $t('quoteTile.buttonText') }}
            </CustomButton>

            <!-- Wenn Vehicle null (code & type müssen bewusst NULL gesetzt sein), dann keine weiteren Details-->
            <!-- Hier auch Info-Text - siehe unten -->
            <CustomButton
              v-if="isVehicleNull"
              id="quote.details-button.null"
              :buttonColor="ButtonColor.SECONDARY"
              @click.="goToQuoteDetailView"
              :disabledState="true"
            >
              {{ $t('quoteTile.buttonText') }}
            </CustomButton>

            <!-- Wenn nicht 112 und nicht bewusst code und type auf null gesetzt, dann wird der Text ausgespielt-->
            <div
              v-if="!isVehicle112 && !isVehicleNull"
              v-dompurify-html="$t('quoteTile.infoNoVehicle')"
            ></div>

            <!-- Wenn bewusst NULL, dann wird angezeigt: "Weitere Details stehen in Kürze zur Verfügung"-->
            <!-- Hier auch Info-Text - siehe oben -->
            <div v-if="isVehicleNull">
              {{ $t('quoteTile.infoDetails') }}
            </div>
          </div>
        </template>
      </Card>
    </span>
  </div>
</template>
<script setup lang="ts">
import ErrorTile from '@/components/common/ErrorTile.vue'
import type { ShowOrDeleteQuotes } from '@/types/policy/PolicyTypes'
import { ButtonColor, Card, CustomButton, GridContent } from 'de-zurich-ui-library'
import { computed, type PropType, ref } from 'vue'
import { formatDate } from '@/utils/policy/PolicyUtils'
import { navigate } from '@/utils/NavigationUtils'
import { useI18n } from 'vue-i18n'
import { formatMonetaryValue } from '@/utils/utils'

const { t } = useI18n()

const props = defineProps({
  partnerProxyId: {
    type: String,
    required: true
  },
  quoteId: {
    type: String,
    required: true
  },
  motorQuote: {
    type: Object as PropType<ShowOrDeleteQuotes>,
    required: true
  },
  isDetailHeader: {
    type: Boolean
  }
})
const loading = ref(false)
const showErrorTile = ref(false)
const showFooter = ref(true)

const subline = computed(() => {
  const tarif = props.motorQuote?.motorContract?.liabilityInsurance?.tarif
    ? props.motorQuote?.motorContract?.liabilityInsurance.tarif
    : 'UNKNOWN'

  return t('quoteTile.tarif.' + tarif)
})

const policyBeginning = computed(() => {
  const beginning = props.motorQuote?.motorContract?.contractBase?.startOfContract
  if (beginning) {
    return formatDate(beginning)
  }
  return '-'
})

const isVehicle112 = computed(() => {
  return props.motorQuote?.motorContract?.vehicleType?.code == '112'
})

const isVehicleNull = computed(() => {
  const vehicleType = props.motorQuote?.motorContract?.vehicleType
  return !vehicleType || (vehicleType.code == null && vehicleType.value == null)
})

function payment(): string | null {
  const grossPremium =
    props.motorQuote?.motorContract?.paymentData?.grossPremiumBasedOnPaymentPeriod?.amount
  const currency =
    props.motorQuote?.motorContract?.paymentData?.grossPremiumBasedOnPaymentPeriod
      ?.currency
  const paymentPeriod = props.motorQuote?.motorContract?.paymentData?.paymentPeriod

  if (grossPremium == null || currency == null) {
    return null
  }

  const premiumAndCurrency = formatMonetaryValue({
    amount: grossPremium,
    currency: currency
  })

  if (paymentPeriod) {
    return premiumAndCurrency + '/' + t('quoteTile.payment.period.' + paymentPeriod)
  }
  return premiumAndCurrency
}

function goToQuoteDetailView(): void {
  const finalDetailUrl: string = '/abschluss/' + props.quoteId
  navigate(finalDetailUrl)
}

const iconStringResolved = 'icon-abschlusstimer'

const firstTitle = computed(() => {
  // for now there is only a static output
  // this will change when it is not only motor quotes
  return 'Kfz-Versicherung'
})
</script>

<style lang="scss" scoped>
:deep(.button.button--cta5) {
  line-height: var(--base-line-height);
  padding: 0 !important;
  font-family: var(--info-label-font-family);
}
</style>
